/**
 * Supervisor Workspace
 *
 * Custom 3-column productivity view for supervisors
 */
import {
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Text,
  TextInput,
  Tabs
} from '@mantine/core';
import TopNav from '../components/TopNav';

export default function SupervisorWorkspace() {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      background: '#f8f9fa'
    }}>
      {/* Top Navigation */}
      <TopNav />

      {/* Content area with 3-column layout */}
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: '280px 1fr 320px',
        overflow: 'hidden'
      }}>
        {/* Left Navbar - Today's Priorities */}
        <div style={{
        background: 'white',
        borderRight: '1px solid #dee2e6',
        overflowY: 'auto',
        padding: 16
      }}>
        <Stack gap="md">
          <Text size="md" fw={600}>Today's Priorities</Text>

          {/* Task Items */}
          <div style={{
            display: 'flex',
            gap: 12,
            padding: 12,
            background: 'white',
            border: '1px solid #e9ecef',
            borderRadius: 4,
            cursor: 'pointer'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 40 }}>
              <div style={{ padding: '2px 6px', background: '#f1f3f5', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>2h</div>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fd7e14', marginTop: 4 }}></div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={600} lineClamp={1}>RBC Q3 Review</Text>
              <Text size="xs" c="dimmed" lineClamp={1}>Capital assessment due EOD</Text>
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: 12,
            padding: 12,
            background: 'white',
            border: '1px solid #e9ecef',
            borderRadius: 4,
            cursor: 'pointer'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 40 }}>
              <div style={{ padding: '2px 6px', background: '#f1f3f5', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>45m</div>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#228be6', marginTop: 4 }}></div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={600} lineClamp={1}>TD Supervisory Letter</Text>
              <Text size="xs" c="dimmed" lineClamp={1}>Final review & approval</Text>
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: 12,
            padding: 12,
            background: 'white',
            border: '1px solid #e9ecef',
            borderRadius: 4,
            cursor: 'pointer'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 40 }}>
              <div style={{ padding: '2px 6px', background: '#f1f3f5', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>1h</div>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#228be6', marginTop: 4 }}></div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={600} lineClamp={1}>BMO Meeting Prep</Text>
              <Text size="xs" c="dimmed" lineClamp={1}>CEO quarterly review @ 2pm</Text>
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: 12,
            padding: 12,
            background: 'white',
            border: '1px solid #e9ecef',
            borderRadius: 4,
            cursor: 'pointer'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 40 }}>
              <div style={{ padding: '2px 6px', background: '#f1f3f5', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>30m</div>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#495057', marginTop: 4 }}></div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={600} lineClamp={1}>CIBC Metrics Review</Text>
              <Text size="xs" c="dimmed" lineClamp={1}>Monthly KPI validation</Text>
            </div>
          </div>

          {/* Workload Forecast */}
          <Text size="md" fw={600} mt="lg">Workload Forecast</Text>

          {['MON', 'TUE', 'WED', 'THU', 'FRI'].map((day, idx) => {
            const values = [95, 70, 60, 80, 40];
            const hours = [9.5, 7, 6, 8, 4];
            return (
              <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Text size="sm" fw={600} c="dimmed" style={{ width: 40 }}>{day}</Text>
                <div style={{ flex: 1, height: 20, background: '#f1f3f5', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                  <div style={{
                    height: '100%',
                    width: `${values[idx]}%`,
                    background: 'linear-gradient(to right, #228be6, #40c057)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    paddingRight: 8
                  }}>
                    <Text size="xs" c="white" fw={600}>{hours[idx]}h</Text>
                  </div>
                </div>
              </div>
            );
          })}

          {/* AI Recommendations */}
          <Text size="md" fw={600} mt="lg">AI Recommendations</Text>
          <div style={{
            background: 'linear-gradient(135deg, #e7f5ff 0%, #f8f9fa 100%)',
            border: '1px solid #74c0fc',
            borderRadius: 8,
            padding: 12
          }}>
            <Group gap="xs" align="flex-start">
              <div style={{
                width: 20,
                height: 20,
                background: '#228be6',
                color: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 'bold',
                flexShrink: 0
              }}>AI</div>
              <div style={{ flex: 1 }}>
                <Text size="xs" fw={600} mb={4}>Pattern Detected</Text>
                <Text size="xs" c="dimmed">3 FIs showing similar liquidity stress. Consider sector-wide guidance.</Text>
              </div>
            </Group>
          </div>
        </Stack>
      </div>

        {/* Main Content - Institution Workspace */}
        <div style={{
          overflowY: 'auto',
          padding: 16
        }}>
        <Stack gap="md">
          {/* Institution Overview */}
          <Card withBorder padding="md">
            <Group justify="space-between" mb="md">
              <div>
                <Text size="lg" fw={700}>Royal Bank of Canada (RBC)</Text>
                <Text size="sm" c="dimmed">D-SIB • Banking Sector 1 • Last Review: Oct 15, 2025</Text>
              </div>
              <Badge color="green" size="sm">COMPLIANT</Badge>
            </Group>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 12,
              marginBottom: 16
            }}>
              <div style={{ textAlign: 'center' }}>
                <Text size="xl" fw={700} lh={1} mb={4}>13.2%</Text>
                <Text size="xs" tt="uppercase" c="dimmed">Capital Ratio</Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Text size="xl" fw={700} lh={1} mb={4}>124%</Text>
                <Text size="xs" tt="uppercase" c="dimmed">LCR</Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Text size="xl" fw={700} lh={1} mb={4}>A+</Text>
                <Text size="xs" tt="uppercase" c="dimmed">Risk Rating</Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Text size="xl" fw={700} lh={1} mb={4}>92%</Text>
                <Text size="xs" tt="uppercase" c="dimmed">E-23 Score</Text>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 8
            }}>
              <Button variant="default" size="sm">View Full Profile</Button>
              <Button variant="default" size="sm">Historical Data</Button>
              <Button variant="filled" size="sm">Start Letter</Button>
              <Button variant="default" size="sm">Schedule Review</Button>
            </div>
          </Card>

          {/* Supervisory Letter Assistant */}
          <Text size="md" fw={600}>Supervisory Letter Assistant</Text>
          <Card withBorder padding="md">
            <Tabs defaultValue="risk">
              <Tabs.List>
                <Tabs.Tab value="risk">Risk Assessment</Tabs.Tab>
                <Tabs.Tab value="capital">Capital Review</Tabs.Tab>
                <Tabs.Tab value="compliance">Compliance Notice</Tabs.Tab>
                <Tabs.Tab value="custom">Custom</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="risk" pt="md">
                <Text size="sm" fw={600} mb="xs">Q3 2025 Risk Assessment - RBC</Text>
                <Text size="xs" c="dimmed" mb="sm">Based on latest supervisory data and peer comparison</Text>

                <div style={{
                  background: '#f8f9fa',
                  padding: 16,
                  borderRadius: 4,
                  marginBottom: 16
                }}>
                  <Text size="sm" style={{ lineHeight: 1.6 }}>
                    Dear [CEO Name],<br /><br />
                    Following our review of RBC's Q3 2025 submissions, we note the following key observations:
                    <ul style={{ margin: '12px 0' }}>
                      <li>Capital adequacy remains strong at 13.2% (min: 10.5%)</li>
                      <li>Liquidity coverage improved to 124% from 118% last quarter</li>
                      <li>Credit risk metrics within acceptable ranges</li>
                    </ul>
                    Areas requiring attention...
                  </Text>
                </div>

                <Group gap="sm">
                  <Button variant="filled" style={{ flex: 1 }}>Generate Full Letter</Button>
                  <Button variant="default">Load Previous</Button>
                  <Button variant="default">Save Draft</Button>
                </Group>
              </Tabs.Panel>
            </Tabs>
          </Card>

          {/* Historical Context */}
          <Text size="md" fw={600}>Recent Activity & Context</Text>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 12
          }}>
            <Card withBorder padding="md">
              <Text size="xs" c="dimmed" mb="xs">Last Letter</Text>
              <Text size="sm" fw={600}>Sep 30, 2025</Text>
              <Text size="xs">Capital adequacy review</Text>
            </Card>
            <Card withBorder padding="md">
              <Text size="xs" c="dimmed" mb="xs">Last Meeting</Text>
              <Text size="sm" fw={600}>Oct 15, 2025</Text>
              <Text size="xs">Quarterly CEO briefing</Text>
            </Card>
            <Card withBorder padding="md">
              <Text size="xs" c="dimmed" mb="xs">Next Review</Text>
              <Text size="sm" fw={600}>Dec 1, 2025</Text>
              <Text size="xs">Annual comprehensive</Text>
            </Card>
          </div>
        </Stack>
      </div>

        {/* Right Aside - Guidelines & Precedents */}
        <div style={{
          background: 'white',
          borderLeft: '1px solid #dee2e6',
          overflowY: 'auto',
          padding: 16
        }}>
        <Stack gap="md">
          {/* Guideline Finder */}
          <Text size="md" fw={600}>Guideline Finder</Text>
          <TextInput placeholder="Search guidelines..." size="sm" />

          <div>
            {[
              { code: 'E-23', title: 'Model Risk Management', desc: 'AI/ML model governance' },
              { code: 'B-6', title: 'Liquidity Principles', desc: 'Liquidity adequacy requirements' },
              { code: 'A-4', title: 'Capital Adequacy', desc: 'Regulatory capital requirements' }
            ].map((guideline) => (
              <div key={guideline.code} style={{
                padding: 8,
                borderBottom: '1px solid #f1f3f5',
                cursor: 'pointer'
              }}>
                <Text size="xs" fw={600} c="blue">{guideline.code}</Text>
                <Text size="sm" fw={600}>{guideline.title}</Text>
                <Text size="xs" c="dimmed">{guideline.desc}</Text>
              </div>
            ))}
          </div>

          {/* Similar Cases */}
          <Text size="md" fw={600} mt="lg">Similar Cases</Text>
          <Card withBorder padding="sm">
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={600}>TD Bank</Text>
              <Text size="xs" c="dimmed">Oct 2025</Text>
            </Group>
            <Text size="xs">Similar capital relief conditions approved with 3-month monitoring</Text>
          </Card>
          <Card withBorder padding="sm">
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={600}>Scotia Bank</Text>
              <Text size="xs" c="dimmed">Sep 2025</Text>
            </Group>
            <Text size="xs">Liquidity stress test requirements enhanced</Text>
          </Card>

          {/* Team Activity */}
          <Text size="md" fw={600} mt="lg">Team Activity</Text>
          <Stack gap="xs">
            {[
              { name: 'John D.', action: 'completed BMO review', time: '10m ago', color: '#40c057' },
              { name: 'Maria S.', action: 'updated CIBC metrics', time: '1h ago', color: '#228be6' },
              { name: 'Risk Team', action: 'flagged sector trend', time: '2h ago', color: '#fd7e14' }
            ].map((activity, idx) => (
              <Group key={idx} gap="xs">
                <div style={{ width: 8, height: 8, background: activity.color, borderRadius: '50%' }}></div>
                <Text size="xs" style={{ flex: 1 }}>
                  <strong>{activity.name}</strong> {activity.action}
                </Text>
                <Text size="xs" c="dimmed">{activity.time}</Text>
              </Group>
            ))}
          </Stack>
          </Stack>
        </div>
      </div>
    </div>
  );
}
